import { useRef } from 'react';

import { useServiceRequest } from '@commenda/api';
import { ServiceRequestListExtensibleFields } from '@commenda/api/service-request/useServiceRequest';
import { Skeleton } from '@commenda/kakashi';
import { SalesTaxFilingTasks } from '@commenda/types/src/sales-tax/types';
import { ServiceRequestDetails } from '@commenda/types/src/service-request/types';
import { TaskParty, TaskStatus } from '@prisma/client';

import { ServiceRequestTimeline } from '../../../../service-request/layout/tabs/components/timeline/ServiceRequestTimeline';
import { getTaskStatus } from '../../../../service-request/utils';

interface IndirectTaxFilingActivityTableProps {
  serviceRequestId: string;
  role?: TaskParty;
}

export const IndirectTaxFilingActivityTable = ({
  serviceRequestId,
  role = TaskParty.CLIENT,
}: IndirectTaxFilingActivityTableProps) => {
  const {
    data: fetchedServiceRequest,
    error: serviceRequestError,
    mutate: refetchServiceRequest,
  } = useServiceRequest(serviceRequestId, {
    extends: [ServiceRequestListExtensibleFields.WORKFLOW_EXECUTION],
  });

  // Hold on to the last loaded service request so a refresh that momentarily
  // resolves without data doesn't collapse the task list into its empty state.
  const lastServiceRequest = useRef<ServiceRequestDetails | null>(null);
  if (fetchedServiceRequest) lastServiceRequest.current = fetchedServiceRequest;
  const serviceRequest = fetchedServiceRequest ?? lastServiceRequest.current;

  const isLoading = Boolean(
    !serviceRequestError && !serviceRequest && serviceRequestId
  );

  if (!serviceRequest) {
    return (
      <div className="flex flex-col justify-center items-center h-full min-h-[200px]">
        {isLoading ? (
          <Skeleton className="h-full w-full flex-1" />
        ) : (
          <div className="flex flex-col gap-xs items-center text-center">
            <h3 className="txt-small-medium text-text-primary">
              No tasks due today!
            </h3>
            <p className="txt-small-regular text-text-tertiary max-w-[494px]">
              Your filing-related tasks will appear here.
            </p>
          </div>
        )}
      </div>
    );
  }

  const taskGroups = (serviceRequest.taskGroups ?? []).map((group) => ({
    ...group,
    tasks: group.tasks.filter(
      (task) =>
        !(
          task.legacyServiceRequestTaskTaskId ===
            SalesTaxFilingTasks.STATE_REGISTRATION &&
          getTaskStatus(task, serviceRequest) === TaskStatus.COMPLETED
        )
    ),
  }));

  return (
    <ServiceRequestTimeline
      serviceRequest={serviceRequest}
      taskGroups={taskGroups}
      role={role}
      mutate={refetchServiceRequest}
      workflowExecution={serviceRequest.workflowExecution}
    />
  );
};
